import { BaseQueryApi, createApi, FetchArgs, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import queryString from 'query-string';
import { toast } from 'react-toastify';

type TokenStorageKeys = {
  accessToken: 'accessToken' | 'superAdminAccessToken';
};

const SUPER_ADMIN_URL_FRAGMENT = 'super-admin';

const isSuperAdminContext = () => (window.location.href || '').includes(SUPER_ADMIN_URL_FRAGMENT);

const getTokenStorageKeys = (isSuperAdmin: boolean): TokenStorageKeys => {
  if (isSuperAdmin) {
    return { accessToken: 'superAdminAccessToken' };
  }

  return { accessToken: 'accessToken' };
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_CONVERSATION_API_URL,
  prepareHeaders: (headers) => {
    const isSuperAdmin = isSuperAdminContext();
    const { accessToken } = getTokenStorageKeys(isSuperAdmin);
    const token = localStorage.getItem(accessToken);
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  },
  paramsSerializer: (params) => {
    return queryString.stringify(params);
  },
});

export const baseQuery = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: object,
) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (!result.error || result.error.status !== 401) {
    return result;
  }

  const { refreshToken } = await import('./baseApi');
  const accessToken = await refreshToken();

  if (!accessToken) {
    return result;
  }

  result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    const errorData = result.error.data;
    const message =
      errorData && typeof errorData === 'object' && 'message' in errorData
        ? String(errorData.message)
        : 'Authentication failed';
    toast.error(message);
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'conversationApi',
  baseQuery,
  endpoints: () => ({}),
});
